import React from 'react';
import {Button, Popover, Progress} from "antd";
import LoadingSpinner from "../../../../components/AntCostomComponents/LoadingSpinner";
import {useTranslation} from "react-i18next";
import styled from "styled-components";

const AcneAnalysisComponent = ({acnes, analysis, hideImages = false, hideProgressBars = false}) => {
    const {t} = useTranslation("common");
    const [isLoading, setIsLoading] = React.useState(true);
    const [detectionData, setDetectionData] = React.useState(null);
    const [imageDimensions, setImageDimensions] = React.useState({});
    const [showDetections, setShowDetections] = React.useState(true);
    const [highlightedDetection, setHighlightedDetection] = React.useState(null);
    const imageRef = React.useRef(null);

    // Get the latest acne analysis (first in the array since they're sorted by date desc)
    const latestAcne = acnes && acnes.length > 0 ? acnes[0] : null;


    React.useEffect(() => {
        const fetchData = async () => {
            if (!latestAcne) {
                setIsLoading(false);
                return;
            }

            setIsLoading(true);

            try {
                const response = await fetch(latestAcne.data_url);
                const result = await response.json();
                setDetectionData(result);
                console.log(`Fetched detection results for acne analysis:`, result);
            } catch (error) {
                console.error(`Error fetching detection results:`, error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
        console.log('Acne analysis component mounted with latest data:', latestAcne);
    }, [latestAcne]);

    const toggleDetections = () => {
        setShowDetections(!showDetections);
    };

    const updateImageDimensions = (img) => {
        if (img) {
            setImageDimensions({
                width: img.clientWidth,
                height: img.clientHeight,
                originalWidth: img.naturalWidth,
                originalHeight: img.naturalHeight
            });
        }
    };

    React.useEffect(() => {
        if (imageRef.current) {
            const updateDims = () => updateImageDimensions(imageRef.current);

            if (imageRef.current.complete) {
                updateDims();
            } else {
                imageRef.current.onload = updateDims;
            }

            // Add resize listener to handle container size changes
            const resizeObserver = new ResizeObserver(updateDims);
            resizeObserver.observe(imageRef.current);

            return () => {
                if (imageRef.current) {
                    resizeObserver.unobserve(imageRef.current);
                }
            };
        }
    }, [imageRef.current]);

    const getTagColor = (className) => {
        const colors = {
            'atrophic-hypertrophic_scar': 'orange',
            'melasma-nevus': '#5acd4b',
            'papule-pustule': '#bd47a1',
            'open-closed_comedo': '#73e5d5',
            'nodule-cyst': '#ff5722',
            'severe-acne-lesions': '#e91e63',
            'default': 'blue'
        };

        return colors[className] || colors.default;
    };

    const mapAcneDetail = (acneType) => {
        const acneDetails = {
            'open-closed_comedo': t("open_closed_comedo") || "Open/Closed Comedo",
            'papule-pustule': t("papule_pustule") || "Papule/Pustule",
            'nodule-cyst': t("nodule_cyst") || "Nodule/Cyst",
            'severe-acne-lesions': t("severe_acne_lesions") || "Severe Acne Lesions",
            'melasma-nevus': t("melasma_nevus") || "Melasma/Nevus",
            'atrophic-hypertrophic_scar': t("atrophic_hypertrophic_scar") || "Atrophic/Hypertrophic Scar",
        };

        return acneDetails[acneType] || t("unknown_acne_type") || "Unknown Acne Type";
    };

    const renderPopoverContent = (detection) => {
        return (
            <div>
                <h4 className="capitalize font-medium mb-2">{detection.class_name.replace(/_/g, ' ')}</h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <div>{t("confidence") || "Confidence"}:</div>
                    <div>{(detection.score * 100).toFixed(1)}%</div>
                    <div>{t("width") || "Width"}:</div>
                    <div>{(detection.bbox[2] - detection.bbox[0]).toFixed(1)}px</div>
                    <div>{t("height") || "Height"}:</div>
                    <div>{(detection.bbox[3] - detection.bbox[1]).toFixed(1)}px</div>
                    <div>{t("location") || "Location"}:</div>
                    <div>{t("x") || "X"}: {detection.bbox[0].toFixed(0)}, {t("y") || "Y"}: {detection.bbox[1].toFixed(0)}</div>
                </div>
            </div>
        );
    };

    const renderDetectionOverlays = () => {
        if (!detectionData || !imageDimensions.width) return null;

        const detections = detectionData.detections || [];
        if (!detections.length) return null;

        // Calculate aspect ratios
        const originalAspectRatio = imageDimensions.originalWidth / imageDimensions.originalHeight;
        const displayAspectRatio = imageDimensions.width / imageDimensions.height;

        // Determine if we need to account for letterboxing due to objectFit: 'cover'
        let scaleX, scaleY, offsetX = 0, offsetY = 0;

        if (displayAspectRatio > originalAspectRatio) {
            // Width is constrained, height may overflow
            const displayHeightIfFull = imageDimensions.width / originalAspectRatio;
            scaleX = imageDimensions.width / imageDimensions.originalWidth;
            scaleY = displayHeightIfFull / imageDimensions.originalHeight;
            // Calculate vertical overflow that gets cropped
            offsetY = (displayHeightIfFull - imageDimensions.height) / 2;
        } else {
            // Height is constrained, width may overflow
            const displayWidthIfFull = imageDimensions.height * originalAspectRatio;
            scaleY = imageDimensions.height / imageDimensions.originalHeight;
            scaleX = displayWidthIfFull / imageDimensions.originalWidth;
            // Calculate horizontal overflow that gets cropped
            offsetX = (displayWidthIfFull - imageDimensions.width) / 2;
        }

        return detections.map((detection, index) => {
            // Scale coordinates accounting for crop
            const x = detection.bbox[0] * scaleX - offsetX;
            const y = detection.bbox[1] * scaleY - offsetY;
            const width = (detection.bbox[2] - detection.bbox[0]) * scaleX;
            const height = (detection.bbox[3] - detection.bbox[1]) * scaleY;

            // Don't render if outside visible area
            if (x + width < 0 || y + height < 0 ||
                x > imageDimensions.width || y > imageDimensions.height) {
                return null;
            }

            const color = getTagColor(detection.class_name);
            const isHighlighted = highlightedDetection === index;

            // Calculate circle dimensions
            const centerX = x + width / 2;
            const centerY = y + height / 2;
            const diagonal = Math.sqrt(width * width + height * height);
            // Use the larger dimension to ensure circle covers the whole detection
            const radius = diagonal / 2 + 5; // Add some padding

            return (
                <Popover
                    key={index}
                    content={renderPopoverContent(detection)}
                    title={`${t("detection") || "Detection"} #${index + 1}`}
                    trigger="hover"
                    placement="top"
                    open={isHighlighted}
                >
                    <div
                        style={{
                            position: 'absolute',
                            left: `${Math.max(0, centerX - radius)}px`,
                            top: `${Math.max(0, centerY - radius)}px`,
                            width: `${Math.min(radius * 2, imageDimensions.width - Math.max(0, centerX - radius))}px`,
                            height: `${Math.min(radius * 2, imageDimensions.height - Math.max(0, centerY - radius))}px`,
                            border: `1px solid ${color}`,
                            borderRadius: '50%',
                            backgroundColor: 'transparent',
                            zIndex: isHighlighted ? 9 : 4
                        }}
                        onMouseEnter={() => setHighlightedDetection(index)}
                        onMouseLeave={() => setHighlightedDetection(null)}
                        onClick={() => setHighlightedDetection(isHighlighted ? null : index)}
                    />
                </Popover>
            );
        });
    };

    if (isLoading) {
        return (
            <div className="w-full flex justify-center items-center py-8">
                <LoadingSpinner/>
            </div>
        );
    }

    if (!latestAcne) {
        return (
            <div className="text-center py-8">
                {t("no_acne_analysis_data_available") || "No acne analysis data available"}
            </div>
        );
    }

    const formattedDate = latestAcne.created_at ? new Date(latestAcne.created_at).toLocaleDateString() : null;

    if (!detectionData) {
        return (
            <div className="mb-8">
                <h3 className="font-bold text-lg mb-3">
                    {t("latest_analysis") || "Latest Analysis"} {formattedDate && `(${formattedDate})`}
                </h3>
                <div className="text-center py-8">
                    {t("no_acne_analysis_data_available") || "No acne analysis data available"}
                </div>
            </div>
        );
    }

    const detections = detectionData.detections || [];
    const visualization = latestAcne.visualization_url;

    console.log('visualization', detectionData);

    // Group detections by class
    const groupedDetections = detections.reduce((acc, detection) => {
        const className = detection.class_name;
        if (!acc[className]) {
            acc[className] = [];
        }
        acc[className].push(detection);
        return acc;
    }, {});

    const detailsPopoverContent = (
        <div>
            {Object.entries(groupedDetections).map(([className, items], idx) => (
                <div className="flex justify-between items-center mb-2" key={idx}>
                    <p className="capitalize text-xs">{mapAcneDetail(className)}</p>
                    <div className="rounded-full flex justify-center items-center" style={{
                        backgroundColor: getTagColor(className),
                        width: '18px',
                        height: '18px',
                    }}>
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="w-full">
            <h3 className="font-bold text-lg mb-3">
                {t("latest_analysis") || "Latest Analysis"} {formattedDate && `(${formattedDate})`}
            </h3>

            <div className={`grid grid-cols-1 ${hideImages ? '' : 'md:grid-cols-2'} gap-4`}>
                {!hideImages && (
                    <div className="flex flex-col gap-3">
                        <div className="text-center flex justify-between gap-3 rounded-xl overflow-hidden relative"
                             style={{maxHeight: '70vh', height: 'auto', width: 'fit-content'}}>
                            <Popover
                                content={detailsPopoverContent}
                                title={t("details") || "Details"}
                                className="absolute right-3 top-3 z-10"
                                placement="bottomRight">
                                <Button type="primary">{t("see_details") || "See Details"}</Button>
                            </Popover>

                            <img
                                src={visualization}
                                ref={imageRef}
                                alt="Detection Visualization"
                                style={{
                                    objectFit: 'contain',
                                    width: '100%',
                                    height: '100%',
                                    display: !showDetections ? 'none' : 'block'
                                }}
                            />

                            <div className="relative w-full" style={{display: !showDetections ? 'block' : 'none'}}>
                                <img
                                    src={analysis.image_url}
                                    ref={imageRef}
                                    alt="Original Image"
                                    style={{
                                        objectFit: 'contain',
                                        width: '100%',
                                        height: '100%',
                                        maxHeight: '60vh'
                                    }}
                                    onLoad={() => {
                                        if (imageRef.current) {
                                            updateImageDimensions(imageRef.current);
                                        }
                                    }}
                                />
                                {renderDetectionOverlays()}
                            </div>
                        </div>

                        <Button
                            type="primary"
                            onClick={toggleDetections}
                        >
                            {showDetections ? t("hide_detections") || 'Hide Detections' : t("show_detections") || 'Show Detections'}
                        </Button>
                    </div>
                )}

                <LesionWrapper className="flex flex-col gap-3 justify-between items-start bg-white p-3 rounded-xl">
                    <h3 className="font-bold text-center text-black w-full">{t("skin_lesion_analysis") || "Skin Lesion Analysis"}</h3>

                    {Object.keys(groupedDetections).length > 0 ? (
                        <>
                            <div className="w-full">
                                <h4 className="font-semibold mb-2">{t("detected_lesions") || "Detected Lesions"}</h4>
                                <div className="m-3">
                                    {Object.entries(groupedDetections).map(([className, items], idx) => (
                                        <div className="flex justify-between items-center mb-2" key={idx}>
                                            <h3 className="capitalize">{mapAcneDetail(className)}</h3>
                                            <div className="rounded-full flex justify-center items-center" style={{
                                                backgroundColor: getTagColor(className),
                                                width: '50px',
                                                height: '50px',
                                            }}>
                                                <p style={{
                                                    color: "white",
                                                    fontSize: '24px',
                                                }}>{items.length}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {!hideProgressBars && (
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-12">
                                        {Object.entries(groupedDetections).map(([className, items], idx) => {
                                            const avgConfidence = items.reduce((sum, item) => sum + item.score, 0) / items.length;
                                            return (
                                                <div className="flex flex-col items-center" key={idx}>
                                                    <Progress
                                                        className="light-progress"
                                                        type="circle"
                                                        percent={(avgConfidence * 100).toFixed(1)}
                                                        strokeColor={getTagColor(className)}
                                                        format={(percent) => `${percent}`}
                                                    />
                                                    <div className="text-center">
                                                        <h3 className="capitalize">{mapAcneDetail(className)}</h3>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="w-full text-center p-4 bg-gray-50 rounded-lg">
                            {t("no_lesions_detected") || "No lesions detected"}
                        </div>
                    )}
                </LesionWrapper>
            </div>
        </div>
    );
};

const LesionWrapper = styled.div`
    * {
        color: black;
    }

    table {
        border-collapse: collapse;
    }

    table td, table th {
        border: 1px solid #e2e8f0;
    }
`;

export default AcneAnalysisComponent;